/*
 * Copyright (c) 2022 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';
import { appConstants } from '../data-model/app-constants';
import { BehaviorSubject, Observable } from 'rxjs';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { ProxyRequest, ServicesData } from '../data-model/proxy.types';

const log = new Logger('BaseService');

@Injectable({
  providedIn: 'root'
})
export class BaseService {
  // property
  private serviceDetail: ServicesData = {};
  private isAllServiceDetailsLoaded = false;

  // Observer
  private serviceDetailsObserver: BehaviorSubject<ServicesData> = new BehaviorSubject(this.serviceDetail);
  getServiceDetails(): Observable<ServicesData> {
    return this.serviceDetailsObserver.asObservable();
  }

  getIsAllServiceDetailsLoaded(): boolean {
    return this.isAllServiceDetailsLoaded;
  }

  constructor(protected dataService?: ProxyDataService) {
    this.init();
  }

  // Service initialize
  init() {
    if (this.serviceDetail && Object.keys(this.serviceDetail).length === 0) {
      // Load Service Map from proxy
      this.loadData(appConstants.appURLs.allService)
        .then((data: ServicesData) => {
          this.isAllServiceDetailsLoaded = true;
          this.serviceDetail = data;
          this.serviceDetailsObserver.next(this.serviceDetail);
          log.debug('Record-Management all service data loaded');
        })
        .catch(ex => {
          log.debug('Record-Management all service data failed to load', ex);
          this.isAllServiceDetailsLoaded = false;
        });
    }
  }

  /**
   * Helper functions
   */
  // Function to call proxy
  loadData(endpoint: string, payload?: ProxyRequest, param?): Promise<any> {
    const promise = payload
      ? this.dataService.post(endpoint, payload)
      : param
      ? this.dataService.get(endpoint, param)
      : this.dataService.get(endpoint);
    promise.catch(error => {
      if (payload) this.logError(payload.toString(), error);
      else this.logError(endpoint, error);
    });

    return promise;
  }

  // Function to print the error log
  private logError(endpoint, err) {
    log.error(["Error Getting '", endpoint, "':"].join(''), JSON.stringify(err));
  }
}
